import { useEffect } from 'react'

const isTypingTarget = (target: EventTarget | null) => {
  const el = target as HTMLElement | null
  if (!el) return false
  const tag = el.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable
}

/**
 * Left/right arrows walk the review, the same way they page the deck.
 *
 * The typing guard is not optional here: rejecting an ad opens an autofocused
 * reason field, and without it every arrow keypress meant for the text would
 * navigate away and discard what had been typed.
 */
export function useReviewKeyNav({
  active,
  onPrev,
  onNext,
}: {
  active: boolean
  onPrev: () => void
  onNext: () => void
}) {
  useEffect(() => {
    if (!active) return undefined

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey) return
      if (isTypingTarget(e.target) || isTypingTarget(document.activeElement)) return

      if (e.key === 'ArrowRight') {
        e.preventDefault()
        onNext()
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault()
        onPrev()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [active, onPrev, onNext])
}
